import type { ParadigmsPageLocale } from '../types'

export const zhParadigms: ParadigmsPageLocale = {
  sectionTag: '应用范式',
  heroTitleA: 'Ontology + AI 在生产里的 ',
  heroTitleSpan: '10 种常见范式',
  heroTitleB: '',
  heroSubtitle:
    '除了 demo 里展示的"报警驱动响应",ontology + AI 还会以几种反复出现的形态落地。按触发方式和产出形态分组列在这里 — 同一套语义底座,完全不同的部署。',
  groupLabels: {
    input: '输入触发',
    runtime: '变更与运行',
    output: '复合产出',
  },
  groupDescriptions: {
    input: '由用户信号启动的范式 — 一个事件、一个文件、一个问题、一个目标。',
    runtime: '当规则变更、工况漂移、或者其他 Agent 在图上 post 时被唤醒的范式。',
    output: '产出比"一个动作"更复杂的复合物 — 一个评分、一条审计链、一份推荐。',
  },
  scenariosLabel: '典型场景',
  interactionLabel: '交互特点',
  bottomCta: {
    sectionTag: '看它们跑起来',
    titleLine1: '其中几个范式已经在 demo 里上线',
    titleLine2: '其余的会在后续版本陆续落地',
    body: '当前 demo 重点演示了目标驱动规划、连续工况(What-if 的一个切片)、以及通过反向归因的可解释性。先去试一下,回头告诉我们你最想看到的下一个范式。',
    button: '进入 Demo',
  },
  paradigms: [
    {
      id: 'event-driven',
      number: '01',
      group: 'input',
      title: '报警 / 事件驱动响应',
      tagline: '实时信号命中约束 → ontology 现场合成响应路径。',
      triggerBadge: '事件',
      outputBadge: '动作链',
      scenarios: [
        { industry: '石油', example: '高含硫泵 H₂S 浓度突增 → 重检材质合规 + 紧急 AVL 替代供应商 + 触发 MOC。' },
        { industry: '金融', example: '可疑欺诈交易 → 冻结 + KYC 重审 + 移交对应客户经理。' },
        { industry: '医疗', example: '患者生命体征越限 → 鉴别诊断排序 + 必要检查 + 用药禁忌。' },
        { industry: '制造', example: '产线质量异常 → 沿批次/工艺反向追溯 + 召回范围 + 供应商索赔依据包。' },
      ],
      interactionNotes: [
        '流式 — 每一条事件都按当前图谱重新求值。',
        '路径是 fire 时刻合成的,不预存为 SOP。',
        '同一条事件在不同目标 / 强约束下会推出完全不同的路径。',
      ],
    },
    {
      id: 'document-driven',
      number: '02',
      group: 'input',
      title: '文件驱动合成',
      tagline: '上传 PDF / 邮件 / 表格,ontology 抽实体并合成处理链。',
      triggerBadge: '文件',
      outputBadge: '动作链',
      scenarios: [
        { industry: '法律', example: '上传合同 → 条款抽取 + 风险评分 + 审批路由 + 会签清单。' },
        { industry: '采购', example: 'RFQ 入库 → 合规矩阵对比 + 供应商 AVL 状态 + 价/期排序。' },
        { industry: '保险', example: '报案材料包 → 伤损分类 + 反欺诈预筛 + 理算员路由。' },
        { industry: '医疗', example: '转诊材料包 → 分诊紧急度 + 专科匹配 + 入院前医嘱。' },
      ],
      interactionNotes: [
        'LLM 做抽取,ontology 做约束和落地 — 路径类型正确。',
        '同一个范式,无论输入是一份 PDF 还是夜批 500 份。',
        '抽取失败会以"路径上的空缺"形式显式暴露,容易发现和打补丁。',
      ],
    },
    {
      id: 'question-grounded',
      number: '03',
      group: 'input',
      title: '问题接地式问答',
      tagline: '自然语言提问沿图谱回答 — 每一个论断都能点回数据源。',
      triggerBadge: '问题',
      outputBadge: '接地答案',
      scenarios: [
        { industry: '合规', example: '"本周能给合同 X 签字吗?" → 沿当事方 + 约束 + 签字权限图遍历。' },
        { industry: '医疗', example: '"患者 X 能开药 Y 吗?" → 诊断 + 在用药 + 相互作用 + 指南。' },
        { industry: '运营', example: '"订单 29117 为什么晚了?" → 多系统贯穿,返回根因 + 责任方 + 预计到达。' },
        { industry: '销售', example: '"哪些客户续约有风险?" → 沿使用 / 客服 / 流失图排序。' },
      ],
      interactionNotes: [
        '胜过纯 RAG:答案带可点击的实体链,而不是只附文档片段。',
        '点答案里的任何实体,可深入其子图。',
        '用户反驳被记为候选约束修订 — 系统从纠错中学习。',
      ],
    },
    {
      id: 'goal-driven',
      number: '04',
      group: 'input',
      title: '目标驱动规划',
      tagline: '声明目标,ontology 逆向规划出满足全部硬约束的动作链。',
      triggerBadge: '目标',
      outputBadge: '计划',
      scenarios: [
        { industry: '采购', example: '"P-1023 找一个 2 周交期的合规机封" → 沿供应商 / 证书 / MOC 图反向规划。' },
        { industry: '学习', example: '"12 周内成为数据分析师" → 排出课程 + 实战项目 + 评估。' },
        { industry: '临床', example: '"把患者稳到能手术" → 有序协议 + 禁忌 + 监测计划。' },
        { industry: '销售', example: '"用当前 pipeline 完成 Q3 配额" → 触达计划 + 内容 + 会议节奏。' },
      ],
      interactionNotes: [
        '反向推理 — 用户看到每一步的"为什么"。',
        '计划在工况或约束变化时实时调整。',
        '多目标并存时,ontology 返回帕累托前沿。',
      ],
    },
    {
      id: 'change-propagation',
      number: '05',
      group: 'runtime',
      title: '变更传播',
      tagline: '改一处规则,影响沿图扇出到所有受影响的资产 / 合同 / 流程,并自动生成变更日志。',
      triggerBadge: '规则更新',
      outputBadge: '影响集',
      scenarios: [
        { industry: '能源', example: 'NACE H₂S 限值升级 → 列出受影响设备 + 在途 RFQ 标记。' },
        { industry: '金融', example: '央行调息 → 全部按揭 PD 重算 + 高风险账户标记。' },
        { industry: '法律', example: '新数据合规法颁布 → 需修订条款的合同 + 需重设计的流程。' },
        { industry: '房地产', example: '城市限购政策更新 → 资产重估 + 受影响租约标记。' },
      ],
      interactionNotes: [
        '相对 SOP 系统的杀手锏 — 一次改动,全图传播。',
        '内建图谱前后差异(diff)视图。',
        '每次传播自动生成可送审的变更日志。',
      ],
    },
    {
      id: 'what-if',
      number: '06',
      group: 'runtime',
      title: 'What-if 仿真',
      tagline: '提一个假设工况,ontology 沿因果链做前向推演。',
      triggerBadge: '假设',
      outputBadge: '推演结果',
      scenarios: [
        { industry: '能源', example: '"如果 6 个月内 H₂S 上升 30%" → 哪些资产撞 SLA、哪些供应商落选。' },
        { industry: '金融', example: '"如果加息 50bp" → 按分段的组合风险、损失计提。' },
        { industry: '运营', example: '"如果产能翻倍" → 瓶颈 + 供应商产能缺口 + 技能缺口。' },
        { industry: '政府', example: '"如果津贴 +20%" → 受益人群 + 预算影响 + 触发部门。' },
      ],
      interactionNotes: [
        '时间感知 — 不只是瞬时重算。',
        '让各方在同一张图上并排对比多个场景。',
        '区分"约束会触发"和"约束有风险" — 用颜色编码。',
      ],
    },
    {
      id: 'continuous-audit',
      number: '07',
      group: 'runtime',
      title: '持续审计',
      tagline: '定时或流式扫一遍,监控漂移、异常、隐性失效的约束。',
      triggerBadge: '定时 / 流式',
      outputBadge: '漂移报告',
      scenarios: [
        { industry: '能源', example: '夜间合规扫描所有资产对照当前标准。' },
        { industry: '金融', example: '跨系统对账 — 同一笔交易在不同系统必须一致。' },
        { industry: '医疗', example: '患者队列对照最新指南,偏离者被标记。' },
        { industry: '政府', example: '月度跨部委公民数据一致性巡检。' },
      ],
      interactionNotes: [
        '区别于事件驱动 — 连续运行,不是单点信号。',
        '汇聚成一块漂移看板,而不是一堆独立告警。',
        '每条发现都附其贡献节点,以及一键"草拟修复"按钮。',
      ],
    },
    {
      id: 'multi-agent',
      number: '08',
      group: 'runtime',
      title: '多 Agent 共享工作区',
      tagline: 'Agent 之间通过读 / 写 ontology 协作,不用硬接 Agent 间协议。',
      triggerBadge: 'Agent 写入',
      outputBadge: '涌现任务',
      scenarios: [
        { industry: 'DevOps', example: '分诊 Agent post 症状,诊断 Agent 加假设,修复 Agent 给补丁。' },
        { industry: '研究', example: '文献 / 数据集 / 实验 Agent 把发现写成 ontology 节点共享。' },
        { industry: '交易', example: '情绪 / 风险 / 执行 Agent 通过共享投资组合图协作。' },
        { industry: '客服', example: '分诊 / KYC / 退款 / 通知 Agent 在工单图上协同。' },
      ],
      interactionNotes: [
        'Agent 订阅图谱 delta,而不是点对点消息。',
        '新 Agent 接入只需声明自己消费 / 产出的节点类型。',
        '人工审批是一种特殊 Agent — 协议一致。',
      ],
    },
    {
      id: 'risk-scoring',
      number: '09',
      group: 'output',
      title: '风险评分',
      tagline: '把多源规则聚合成单一实体的评分,任何输入变化都触发全图重算。',
      triggerBadge: '任意输入变化',
      outputBadge: '评分',
      scenarios: [
        { industry: '金融', example: '客户 / 对手方 / 贷款风险评分,带完整归因。' },
        { industry: '采购', example: '供应商风险指数,跨财务 / 运营 / ESG 多维度聚合。' },
        { industry: '能源', example: '设备健康分:腐蚀 + 振动 + 设备龄 + 近期停机历史。' },
        { industry: '医疗', example: '患者急重度评分:生命体征 + 化验 + 合并症。' },
      ],
      interactionNotes: [
        '评分始终可解释 — 每个贡献因子一键展开。',
        '反应式 — 调整任一输入,评分及其贡献因子实时更新。',
        '同一评分对不同观众用不同单位呈现(成本 / 生命 / 天数)。',
      ],
    },
    {
      id: 'explainability',
      number: '10',
      group: 'output',
      title: '审计链 / 可解释回放',
      tagline: '给定任何决策,反向展开为"目标 → 对象 → 推理 → 约束 → 数据源"完整链。',
      triggerBadge: '事后查询',
      outputBadge: '因果链',
      scenarios: [
        { industry: '能源', example: '"为什么禁了 Plan 53A?" → 引用标准 + 工况读数。' },
        { industry: '金融', example: '"这笔贷款为什么拒了?" → 规则 + KYC 字段 + 抵押图。' },
        { industry: '医疗', example: '"为什么没开药 Y?" → 禁忌规则 + 相互作用图 + 指南引用。' },
        { industry: '监管', example: '监管想问什么,ontology 都能带引用 + 可点击数据出处回答。' },
      ],
      interactionNotes: [
        '对每一个历史决策可用 — 不只是当前那一个。',
        '产出是图 + 文字 + 引用数据源,全部可点击。',
        '同一条链反向反哺治理审查 — 例如"这条规则跟那条冲突"。',
      ],
    },
  ],
}
